import type { FilterField, FilterCondition } from '@vnetwork/ui/data';
import type { CustomerTicketRow } from '@/api/customer-tickets';
import { humanize } from '../status-tones';

/** Distinct, sorted values from rows via an accessor (drops empties). */
function distinct(rows: CustomerTicketRow[], pick: (r: CustomerTicketRow) => string | undefined | null): string[] {
  const set = new Set<string>();
  for (const r of rows) {
    const v = pick(r);
    if (v != null && v !== '') set.add(v);
  }
  return Array.from(set).sort();
}

/**
 * FilterBuilder fields for the tickets table, derived from the loaded rows so
 * options stay accurate: Status · Priority · Product group. Applied client-side,
 * mirroring the Service SLAs table's filter pattern.
 */
export function buildTicketFilterFields(rows: CustomerTicketRow[]): FilterField[] {
  const fields: FilterField[] = [];

  const statuses = distinct(rows, (r) => r.ticket_status);
  if (statuses.length > 1) {
    fields.push({
      value: 'ticket_status',
      label: 'Status',
      input: 'select',
      options: statuses.map((s) => ({ value: s, label: humanize(s) })),
    });
  }

  const priorities = distinct(rows, (r) => r.ticket_priority);
  if (priorities.length > 1) {
    fields.push({
      value: 'ticket_priority',
      label: 'Priority',
      input: 'select',
      options: priorities.map((p) => ({ value: p, label: humanize(p) })),
    });
  }

  const groups = distinct(rows, (r) => r.product_group_name);
  if (groups.length > 1) {
    fields.push({
      value: 'product_group_name',
      label: 'Product Group',
      input: 'select',
      options: groups.map((g) => ({ value: g, label: g })),
    });
  }

  return fields;
}

/** True if the row satisfies every applied condition (values OR within a field). */
export function matchesTicketConditions(row: CustomerTicketRow, conditions: FilterCondition[]): boolean {
  for (const c of conditions) {
    const values = c.value.split(',').map((v) => v.trim()).filter(Boolean);
    if (values.length === 0) continue;
    let actual: string | null;
    switch (c.field) {
      case 'ticket_status':
        actual = row.ticket_status ?? null;
        break;
      case 'ticket_priority':
        actual = row.ticket_priority ?? null;
        break;
      case 'product_group_name':
        actual = row.product_group_name ?? null;
        break;
      default:
        actual = null;
    }
    if (actual == null || !values.includes(actual)) return false;
  }
  return true;
}
